<template>
  <div
    class="banner-preview-container card p-3"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @click="redirectToEditor">
    <div class="banner-dimensions badge bg-blue" v-if="renderedImage">
      {{ renderedImage.width }}x{{ renderedImage.height }}
    </div>
    <div
      v-if="notEditable && props.showNoEditFlag"
      class="banner-not-editable badge badge-sm bg-yellow"
      v-tooltip="{ content: $t('orders.details.banners.not_editable') }">
      <IconPencilCancel class="text-white" :size="16" />
    </div>

    <div v-if="renderedImage" class="banner-preview-image d-flex justify-content-center align-items-center">
      <img :src="renderedImage.proxy_url" style="max-height: 100%" />

      <video
        v-if="isHovered && renderedVideo && [1, 6, 7].includes(renderedVideo.status)"
        class="banner-preview-video"
        :class="{ 'is-ready': videoReady }"
        autoplay
        loop
        muted
        playsinline
        @canplay="videoReady = true">
        <source :src="getStreamVideoUrl(renderedVideo.hash, 'mp4')" type="video/mp4" />
        <source :src="getStreamVideoUrl(renderedVideo.hash, 'webm')" type="video/webm" />
      </video>

      <div class="banner-preview-overlay" v-if="renderedVideo && ![1, 6, 7].includes(renderedVideo.status)">
        <div v-if="renderedVideo.status < 0">
          <IconAlertHexagon :size="30" class="d-block mb-2 mx-auto"></IconAlertHexagon>
          <strong>{{ $t('campaigns.media.video_banner') }}</strong
          ><br />
          {{ $t('campaigns.media.video_failed') }}
        </div>
        <div v-else>
          <div class="spinner-border mb-3" role="status"></div>
          <br />
          <strong>{{ $t('campaigns.media.video_banner') }}</strong
          ><br />
          {{ $t('campaigns.media.video_processing') }}
        </div>
      </div>
    </div>
    <div v-else class="unfinished-banner-container d-flex flex-column gap-2 justify-content-center align-items-center">
      <template v-if="renderedError">
        <IconExclamationCircleFilled :size="40" class="text-danger" v-tooltip="{ content: renderedError }" />
        <p>{{ $t('campaigns.media.banner_failed') }}</p>
      </template>
      <template v-else>
        <div class="spinner-border" role="status"></div>
        <br />
        {{ $t('campaigns.media.banner_processing') }}
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { fetchBannerById } from '@/api/banner';
import { hostname } from '@/api/client';
import { useRouter } from 'vue-router';
import type { IBanner } from '@/types/banner';
import { IconAlertHexagon, IconExclamationCircleFilled, IconPencilCancel } from '@tabler/icons-vue';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

const router = useRouter();
const props = defineProps<{ banner: IBanner; showNoEditFlag?: boolean }>();

const isHovered = ref(false);
const videoReady = ref(false);
const hasError = ref(false);

watch(isHovered, (val) => {
  if (!val) videoReady.value = false;
});

const singleFetchedBannerData = ref<IBanner | null>(null);

/* -------------------------------- COMPUTED -------------------------------- */
const notEditable = computed(() => {
  return !props.banner.designer_template_id;
});
const effectiveBannerData = computed(() => {
  return singleFetchedBannerData.value ?? props.banner;
});

const renderedVideo = computed(() => {
  return effectiveBannerData.value.render_video?.data;
});

const renderedImage = computed(() => {
  return effectiveBannerData.value.render_image?.data;
});

const renderedError = computed(() => {
  if (!effectiveBannerData.value.render_error) return '';
  const destructError = effectiveBannerData.value.render_error.split('Error:');
  if (destructError.length > 1) return destructError[1].split(':')[0] || 'Unknown error';
  return effectiveBannerData.value.render_error;
});

function updateBannerData() {
  fetchBannerById(props.banner.id)
    .then((response) => {
      singleFetchedBannerData.value = response;
    })
    .catch((error) => {
      console.error('Error fetching banner data:', error);
      hasError.value = true;
    });
}

let intervalId: number | undefined;

onMounted(() => {
  intervalId = window.setInterval(() => {
    if (
      hasError.value ||
      (renderedImage.value &&
        (!renderedVideo.value || renderedVideo.value.status < 0 || [1, 6, 7].includes(renderedVideo.value.status))) ||
      (renderedVideo.value?.status !== 0 && (renderedVideo.value?.status ?? 0) < 0)
    ) {
      if (intervalId !== undefined) {
        clearInterval(intervalId);
        intervalId = undefined;
      }
      return;
    }

    updateBannerData();
  }, 5000); // every 5 seconds
});

onUnmounted(() => {
  if (intervalId !== undefined) {
    clearInterval(intervalId);
  }
});

/* --------------------------------- METHODS -------------------------------- */

function redirectToEditor() {
  if (notEditable.value) return;
  router.push({
    name: 'banners.sandbox',
    query: {
      jobId: props.banner.job_id,
      bannerId: props.banner.id,
    },
  });
}
function getStreamVideoUrl(videoHash: string | undefined, extension: string) {
  if (!videoHash) throw Error('No video streaming URL found');

  return `${hostname}/v1/media/videos/${videoHash}/stream?extension=${extension}`;
}
</script>

<style scoped lang="scss">
.banner-preview-container {
  background-color: #f2f2f2;
  position: relative;
  width: 100%;
  height: 100%;
}

.banner-preview-image {
  position: relative;
  width: 100%;
  height: 100%;
}

.unfinished-banner-container {
  aspect-ratio: 4/3;
  background-color: #f8f9fa;
  border-radius: 5px;
}

.banner-preview-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #f2f2f2;
  opacity: 0;
  transition: opacity 0.2s ease;

  &.is-ready {
    opacity: 1;
  }
}

.banner-preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.banner-not-editable {
  position: absolute;
  top: 3px;
  left: 3px;
  z-index: 5;
}

.banner-dimensions {
  display: inline;
  position: absolute;
  top: 3px;
  right: 3px;
  z-index: 5;
}
</style>
